import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import "@/App.css";
import { Toaster } from "sonner";
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AccountPage from "./pages/AccountPage";
import AdminPage from "./pages/AdminPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import ContactPage from "./pages/ContactPage";
import PoliciesPage from "./pages/PoliciesPage";
import AuthCallback from "./components/AuthCallback";
import WishlistPage from "./pages/WishlistPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import OurStoryPage from "./pages/OurStoryPage";
import AuthDebugPage from "./pages/AuthDebugPage";
import { MessageCircle } from "lucide-react";
import AnnouncementBar from "./components/AnnouncementBar";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

function resolveImageUrl(url) {
  if (!url) return url;
  if (url.startsWith("/api/files/")) return `${BACKEND_URL}${url}`;
  try {
    const parsed = new URL(url);
    if (parsed.pathname.startsWith("/api/files/")) {
      return `${BACKEND_URL}${parsed.pathname}`;
    }
  } catch (_) { /* not a valid URL, return as-is */ }
  return url;
}

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(data);
    return data;
  };

  const register = async (email, password, name, phone) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name, phone }, { withCredentials: true });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-zinc-900 border-r-transparent"></div>
          <p className="mt-4 text-sm text-zinc-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return children;
}

// ── WhatsApp floating button — appears on every page ─────────────────────────
// Replace 919999999999 with your real number e.g. 919876543210 for +91 98765 43210

function WhatsAppButton() {
  return (
    <a
      href="https://wa.me/917240130440?text=Hi%20Rivora!%20I%20have%20a%20question%20about%20your%20jewellery."
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat on WhatsApp"
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[#25D366] text-white px-4 py-3 shadow-lg hover:bg-[#1ebe57] transition-colors"
    >
      <MessageCircle size={20} />
      <span className="text-xs font-medium uppercase tracking-widest hidden sm:inline">
        Chat to Order
      </span>
    </a>
  );
}

function AppRouter() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  // CRITICAL FIX: Check for session_id in URL hash
  // Mobile browsers may not preserve location.hash the same way, so check window.location.hash directly
  const currentHash = window.location.hash;
  const hasSessionId = currentHash?.includes('session_id=');
  
  // Debug logging for mobile troubleshooting
  useEffect(() => {
    if (hasSessionId) {
      console.log('Auth callback detected:', {
        hash: currentHash,
        pathname: location.pathname,
        fullUrl: window.location.href,
        isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
      });
    }
  }, [hasSessionId, currentHash, location.pathname]);

  if (hasSessionId) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/story" element={<OurStoryPage />} />
      <Route path="/product/:productId" element={<ProductDetailPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
      <Route path="/order-success" element={<ProtectedRoute><OrderSuccessPage /></ProtectedRoute>} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/policies" element={<PoliciesPage />} />
      <Route path="/wishlist" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth-debug" element={<AuthDebugPage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AnnouncementBar/>

        <Toaster position="top-center" richColors />
        <AppRouter />
        {/* WhatsApp button renders outside routes so it shows on every page */}
        <WhatsAppButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL, resolveImageUrl };